import { useContext, useEffect, useState } from 'react';
import { Badge, Button, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { BiLinkAlt, BiPencil, BiSort, BiTrash } from 'react-icons/bi';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LoaderContext } from '../../../App';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import PaginationComponent from '../../../components/Pagination/Pagination';
import { SORT_ORDER_DESC } from '../../../core/constants/common';
import GHGEmissionMasterDataService from '../../../core/service/GHGEmissionMasterDataService';
import SourceFormModal from './SourceFormModal';

const GHGEmissionSources = ({ t, datasetName, version, searchKey, onViewFactors, showAddModal, onHideAddModal, companyId = null }) => {
  const { setLoaderDisplay } = useContext(LoaderContext);

  const [sources, setSources] = useState([]);
  const [sourcesTotalCount, setSourcesTotalCount] = useState(0);
  const [sourcesCurrentPage, setSourcesCurrentPage] = useState(1);
  const [sourcesItemsPerPage, setSourcesItemsPerPage] = useState(10);
  const [order, setOrder] = useState(SORT_ORDER_DESC);
  const [sortField, setSortField] = useState('name');
  const [showAddSourceModal, setShowAddSourceModal] = useState(false);
  const [showEditSourceModal, setShowEditSourceModal] = useState(false);
  const [showDeleteSourceModal, setShowDeleteSourceModal] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [sourceToDelete, setSourceToDelete] = useState(null);

  useEffect(() => {
    if (datasetName && version) {
      loadSources();
    } else {
      setSources([]);
      setSourcesTotalCount(0);
    }
  }, [datasetName, version, sourcesCurrentPage, sourcesItemsPerPage, searchKey, sortField, order]);

  useEffect(() => {
    if (showAddModal !== undefined) {
      setShowAddSourceModal(showAddModal);
    }
  }, [showAddModal]);

  const loadSources = () => {
    if (!datasetName || !version) return;

    setLoaderDisplay(true);
    const params = {
      pageIndex: sourcesCurrentPage - 1,
      pageSize: sourcesItemsPerPage,
      sort: `${sortField},${order}`,
    };

    if (searchKey) {
      params.name = searchKey;
    }

    GHGEmissionMasterDataService.listSources(datasetName, version, params, companyId)
      .then((response) => {
        if (response.status === 200) {
          const data = response.data;
          const sourcesList = data.content || data.data || data || [];
          setSources(sourcesList);
          const totalCount = parseFloat(response.headers["x-total-count"]);
          setSourcesTotalCount(totalCount);
        }
      })
      .catch((error) => {
      })
      .finally(() => {
        setLoaderDisplay(false);
      });
  };

  const handleEditSource = (source) => {
    setSelectedSource(source);
    setShowEditSourceModal(true);
  };

  const handleDeleteSource = (source) => {
    setSourceToDelete(source);
    setShowDeleteSourceModal(true);
  };

  const handleDeleteSourceConfirm = (confirmed) => {
    if (!confirmed || !sourceToDelete) {
      setShowDeleteSourceModal(false);
      setSourceToDelete(null);
      return;
    }

    setLoaderDisplay(true);
    GHGEmissionMasterDataService.deleteSource(datasetName, version, sourceToDelete.id, companyId)
      .then((response) => {
        if (response.status === 200 || response.status === 204) {
          toast.success(t("GHGEmissionSources.deleteSuccess.label"), {
            position: toast.POSITION.TOP_RIGHT,
          });
          loadSources();
        }
      })
      .catch((error) => {
      })
      .finally(() => {
        setLoaderDisplay(false);
        setShowDeleteSourceModal(false);
        setSourceToDelete(null);
      });
  };

  const handleSourceFormSuccess = () => {
    setShowAddSourceModal(false);
    setShowEditSourceModal(false);
    setSelectedSource(null);
    loadSources();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const handleViewFactors = (source) => {
    if (onViewFactors) {
      onViewFactors(source);
    }
  };

  const handleSort = (field) => {
    if (sortField === field && order === 'asc') {
      setOrder('desc');
    } else {
      setOrder('asc');
    }
    setSortField(field);
  };

  return (
    <>
      <div className="table-container ">
        <div className="table-responsive GHGemission-sources-table">
          <Table bordered hover  >
            <thead>
              <tr>
                <th>
                  <Link to="#" onClick={() => handleSort('name')}>
                    {t("GHGEmissionSources.table.name.label")}
                    <BiSort />
                  </Link>
                </th>
                <th>
                  <Link to="#" onClick={() => handleSort('category')}>
                    {t("GHGEmissionSources.table.category.label")}
                    <BiSort />
                  </Link>
                </th>
                <th>
                  <Link to="#" className='white-nowrap' onClick={() => handleSort('sourceType')}>
                    {t("GHGEmissionSources.table.sourceType.label")}
                    <BiSort />
                  </Link>
                </th>
                <th>
                  <Link to="#" className='white-nowrap' onClick={() => handleSort('geography')}>
                    {t("GHGEmissionSources.table.geography.label")}
                    <BiSort />
                  </Link>
                </th>
                <th>
                  <Link to="#" className='white-nowrap' onClick={() => handleSort('primaryScope')}>
                    {t("GHGEmissionSources.table.primaryScope.label")}
                    <BiSort />
                  </Link>
                </th>
                <th>{t("GHGEmissionSources.table.sector.label")}</th>
                <th className='white-nowrap'>{t("GHGEmissionSources.table.validFrom.label")}</th>
                <th className='white-nowrap'>{t("GHGEmissionSources.table.validTo.label")}</th>
                <th>{t("Actions.label")}</th>
              </tr>
            </thead>
            <tbody>
              {sources.length > 0 ? (
                sources.map((source) => (
                  <tr key={source.id}>
                    <td>

                      <Link
                        to="#"
                        className="GHGemission-SourceName-link"
                        onClick={(e) => {
                          e.preventDefault();
                          handleViewFactors(source);
                        }}
                        data-testid={`factor-link-${source.id}`}
                      >
                        {source.name}
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>{t("GHGEmissionSources.viewFactors.label")}</Tooltip>}
                        >
                          <span>
                            <BiLinkAlt />
                          </span>
                        </OverlayTrigger>

                      </Link>
                    </td>
                    <td>{source.category || '-'}</td>
                    <td>{source.sourceType || '-'}</td>
                    <td>{source.geography || '-'}</td>
                    <td>
                      <Badge bg="info">{source.primaryScope || '-'}</Badge>
                    </td>
                    <td>{source.sector || '-'}</td>
                    <td>{formatDate(source.validFrom)}</td>
                    <td>{formatDate(source.validTo)}</td>
                    <td>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{t("Edit.label")}</Tooltip>}
                      >
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => handleEditSource(source)}
                          className="p-1"
                          data-testid={`edit-source-${source.id}`}
                        >
                          <BiPencil />
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{t("Delete.label")}</Tooltip>}
                      >
                        <Button
                          variant="link"
                          size="sm"
                          className="text-danger p-1"
                          onClick={() => handleDeleteSource(source)}
                          data-testid={`delete-source-${source.id}`}
                        >
                          <BiTrash />
                        </Button>
                      </OverlayTrigger>

                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center">
                    <div className="Nodatafound_box">
                      <h6>{t("NoData.label")}</h6>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {sourcesTotalCount > 10 && (
        <div className="p-2 List_footer_count">
          <PaginationComponent
            itemsTotalCount={sourcesTotalCount}
            itemsPerPage={sourcesItemsPerPage}
            currentPage={sourcesCurrentPage}
            setCurrentPage={setSourcesCurrentPage}
            setItemsPerPage={setSourcesItemsPerPage}
            pageClicked={(pageNum, perPage) => {
              if (pageNum && pageNum !== sourcesCurrentPage) {
                setSourcesCurrentPage(pageNum);
              }
              if (perPage && perPage !== sourcesItemsPerPage) {
                setSourcesItemsPerPage(perPage);
                setSourcesCurrentPage(1);
              }
            }}
            alwaysShown={true}
          />
        </div>
      )}

      {/* Modals */}
      <SourceFormModal
        show={showAddSourceModal || showEditSourceModal}
        onHide={() => {
          setShowAddSourceModal(false);
          setShowEditSourceModal(false);
          setSelectedSource(null);
          if (onHideAddModal) {
            onHideAddModal();
          }
        }}
        datasetName={datasetName}
        version={version}
        source={selectedSource}
        onSuccess={handleSourceFormSuccess}
        companyId={companyId}
      />

      {showDeleteSourceModal && (
        <ConfirmationModal
          modalShow={true}
          messageText={t("GHGEmissionSources.deleteModal.message.label")}
          callbackModal={handleDeleteSourceConfirm}
          title={t("GHGEmissionSources.deleteModal.title.label")}
          confirmBtn={t("Delete.label")}
          CancelBtn={t("Cancel.label")}
        />
      )}
    </>
  );
};

export default withTranslation()(GHGEmissionSources);
